// mobile/components/CreateSeasonModal.js
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Pressable,
  TextInput, Platform, KeyboardAvoidingView, ScrollView, Animated, Easing
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
function timeSafeDay(n) { return n; }

const SEASON_PRESETS = [
  { key: 'M1', label: 'M1 (Apr–Okt)', start: { m: 4, d: timeSafeDay(15) }, end: { m: 10, d: timeSafeDay(15) } },
  { key: 'M2', label: 'M2 (Okt–Feb)', start: { m: 10, d: timeSafeDay(15) }, end: { m: 2, d: timeSafeDay(15) } },
  { key: 'M3', label: 'M3 Ops (Feb–Apr)', start: { m: 2, d: timeSafeDay(15) }, end: { m: 4, d: timeSafeDay(15) } },
];

const buildPresetRange = (yearAnchor, presetKey) => {
  const p = SEASON_PRESETS.find(x => x.key === presetKey);
  if (!p) return null;
  const from = ymd(yearAnchor, p.start.m, p.start.d);
  const endYear = (p.end.m < p.start.m) ? yearAnchor + 1 : yearAnchor;
  const to = ymd(endYear, p.end.m, p.end.d);
  return { from, to, label: `${p.label} • ${from}→${to}` };
};

const Chip = memo(function Chip({ label, active, onPress, color, soft, icon, border }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[
        styles.chip,
        { borderColor: border },
        active && { borderColor: color, backgroundColor: soft }
      ]}
      activeOpacity={0.85}
    >
      {!!icon && <Ionicons name={icon} size={14} color={active ? color : '#667085'} style={{ marginRight:6 }} />}
      <Text style={[styles.chipText, active && { color }]}>{label}</Text>
    </TouchableOpacity>
  );
});

function CreateSeasonModal({
  visible,
  onClose,
  onSubmit, // async ({ name, start_date, end_date, preset_key, year_anchor, mode })
  colors,
  radius = 14,
  pad = 12,
  accent,
  accentSoft,
}) {
  const C = colors || stylesVars.colors;
  const A = accent || C.accent;
  const AS = accentSoft || C.accentSoft;

  const isWeb = Platform.OS === 'web';

  const [mode, setMode] = useState('preset'); // 'preset' | 'manual'
  const [presetKey, setPresetKey] = useState('M1');
  const [presetYear, setPresetYear] = useState(new Date().getFullYear());
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [name, setName] = useState('');

  const nameRef = useRef(null);

  // anim values
  const anim = useRef(new Animated.Value(0)).current;

  const suggestedName = useMemo(() => {
    if (mode === 'preset') return `${presetKey} ${presetYear}`;
    return '';
  }, [mode, presetKey, presetYear]);

  // open/close animation + reset state only on open
  useEffect(() => {
    if (!visible) return;

    // reset
    const y = new Date().getFullYear();
    setMode('preset');
    setPresetKey('M1');
    setPresetYear(y);

    const rr = buildPresetRange(y, 'M1');
    setStart(rr?.from || '');
    setEnd(rr?.to || '');
    setName('');

    anim.setValue(0);
    Animated.timing(anim, {
      toValue: 1,
      duration: 160,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true
    }).start();

    // focus input (web: next frame supaya ga “loncat”)
    const t = setTimeout(() => {
      nameRef.current?.focus?.();
    }, isWeb ? 80 : 200);

    return () => clearTimeout(t);
  }, [visible, anim, isWeb]);

  useEffect(() => {
    if (!visible) return;
    if (mode !== 'preset') return;
    const rr = buildPresetRange(presetYear, presetKey);
    if (rr) { setStart(rr.from); setEnd(rr.to); }
  }, [visible, mode, presetKey, presetYear]);

  const canSubmit = useMemo(() => !!start && !!end, [start, end]);

  const handleSubmit = async () => {
    const payload = {
      name: (name || suggestedName || '').trim() || null,
      start_date: start || null,
      end_date: end || null,
      preset_key: mode === 'preset' ? presetKey : null,
      year_anchor: mode === 'preset' ? presetYear : null,
      mode
    };
    await onSubmit?.(payload);
  };

  const closeWithAnim = () => {
    Animated.timing(anim, {
      toValue: 0,
      duration: 120,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true
    }).start(({ finished }) => {
      if (finished) onClose?.();
    });
  };

  if (!visible) return null;

  const backdropOpacity = anim.interpolate({ inputRange: [0, 1], outputRange: [0, 1] });

  // Web: modal muncul dari sedikit atas (lebih natural)
  // Mobile: modal naik dari bawah
  const translateY = anim.interpolate({
    inputRange: [0, 1],
    outputRange: isWeb ? [12, 0] : [22, 0]
  });

  const scale = isWeb
    ? anim.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1] })
    : 1;

  const sheetStyle = [
    styles.sheet,
    {
      borderRadius: radius + 6,
      padding: pad,
      borderColor: C.border,
      backgroundColor: C.card,
      transform: [{ translateY }, ...(isWeb ? [{ scale }] : [])],
      opacity: anim
    }
  ];

  return (
    <View style={styles.overlay} pointerEvents="box-none">
      <Animated.View style={[styles.backdrop, { opacity: backdropOpacity }]}>
        <Pressable style={StyleSheet.absoluteFillObject} onPress={closeWithAnim} />
      </Animated.View>

      <KeyboardAvoidingView
        behavior={Platform.select({ ios: 'padding', android: 'height', default: undefined })}
        style={[styles.kav, isWeb ? styles.kavWeb : styles.kavMobile]}
      >
        <Animated.View style={sheetStyle}>
          <View style={styles.headRow}>
            <View style={{ flexDirection:'row', alignItems:'center' }}>
              <View style={[styles.iconPill, { borderColor: A, backgroundColor: AS }]}>
                <Ionicons name="leaf-outline" size={16} color={A} />
              </View>
              <View style={{ marginLeft:10 }}>
                <Text style={[styles.hTitle, { color: C.text }]}>Mulai Musim</Text>
                <Text style={[styles.hSub, { color: C.muted }]}>Pilih preset M1/M2/M3 atau isi manual.</Text>
              </View>
            </View>

            <TouchableOpacity onPress={closeWithAnim} style={[styles.closeBtn, { borderColor: C.border }]} activeOpacity={0.85}>
              <Ionicons name="close" size={18} color={C.muted} />
            </TouchableOpacity>
          </View>

          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingBottom: 6 }}
            style={{ marginTop: 10, maxHeight: isWeb ? 520 : 420 }}
          >
            <View style={{ flexDirection:'row', columnGap:8 }}>
              <Chip
                label="Preset (M1/M2/M3)"
                icon="calendar-number-outline"
                active={mode === 'preset'}
                onPress={() => setMode('preset')}
                color={A}
                soft={AS}
                border={C.border}
              />
              <Chip
                label="Manual"
                icon="create-outline"
                active={mode === 'manual'}
                onPress={() => setMode('manual')}
                color={A}
                soft={AS}
                border={C.border}
              />
            </View>

            {mode === 'preset' && (
              <View style={[styles.block, { borderColor: C.border, backgroundColor:'#fff' }]}>
                <View style={styles.blockHead}>
                  <View style={{ flexDirection:'row', alignItems:'center' }}>
                    <Ionicons name="calendar-outline" size={16} color={A} />
                    <Text style={[styles.blockTitle, { color: C.text }]}>Pola 3 Musim</Text>
                  </View>

                  <View style={{ flexDirection:'row', alignItems:'center' }}>
                    <TouchableOpacity onPress={() => setPresetYear(y => y - 1)} style={[styles.yearBtn, { borderColor: C.border }]} activeOpacity={0.85}>
                      <Ionicons name="chevron-back" size={18} color={A} />
                    </TouchableOpacity>
                    <View style={[styles.yearPill, { borderColor: C.border }]}>
                      <Text style={{ fontWeight:'900', color: C.text }}>{presetYear}</Text>
                    </View>
                    <TouchableOpacity onPress={() => setPresetYear(y => y + 1)} style={[styles.yearBtn, { borderColor: C.border }]} activeOpacity={0.85}>
                      <Ionicons name="chevron-forward" size={18} color={A} />
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={{ flexDirection:'row', flexWrap:'wrap', marginTop: 6 }}>
                  {SEASON_PRESETS.map(p => (
                    <TouchableOpacity
                      key={p.key}
                      onPress={() => setPresetKey(p.key)}
                      style={[
                        styles.presetChip,
                        { borderColor: C.border },
                        presetKey === p.key && { borderColor: A, backgroundColor: AS }
                      ]}
                      activeOpacity={0.85}
                    >
                      <Ionicons name="leaf-outline" size={14} color={presetKey === p.key ? A : C.muted} />
                      <Text style={{ marginLeft:6, fontWeight:'800', color: presetKey === p.key ? A : C.text }}>
                        {p.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <View style={styles.hintRow}>
                  <Ionicons name="information-circle-outline" size={16} color={C.muted} />
                  <Text style={[styles.hintText, { color: C.muted }]}>
                    M3 opsional (tanam Feb–Mar, panen Apr). Kalau tanam April, itu sudah masuk M1.
                  </Text>
                </View>
              </View>
            )}

            <View style={{ marginTop: 10 }}>
              <Text style={[styles.label, { color: C.text }]}>Mulai (YYYY-MM-DD)</Text>
              <TextInput
                value={start}
                onChangeText={setStart}
                placeholder="2026-04-15"
                placeholderTextColor={C.muted}
                style={[styles.input, { borderColor: C.border, color: C.text }]}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="numbers-and-punctuation"
              />

              <Text style={[styles.label, { color: C.text, marginTop: 10 }]}>Selesai (YYYY-MM-DD)</Text>
              <TextInput
                value={end}
                onChangeText={setEnd}
                placeholder="2026-10-15"
                placeholderTextColor={C.muted}
                style={[styles.input, { borderColor: C.border, color: C.text }]}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="numbers-and-punctuation"
              />
            </View>

            <View style={{ marginTop: 10 }}>
              <Text style={[styles.label, { color: C.text }]}>Nama musim (opsional)</Text>
              <TextInput
                ref={nameRef}
                value={name}
                onChangeText={setName}
                placeholder={suggestedName || 'Contoh: Musim Utama 2026'}
                placeholderTextColor={C.muted}
                style={[styles.input, { borderColor: C.border, color: C.text }]}
                autoCapitalize="words"
                autoCorrect={false}
                returnKeyType="done"
              />
              <Text style={[styles.helper, { color: C.muted }]}>
                Kosongkan kalau mau pakai default (misal: {suggestedName || 'M1 2026'}).
              </Text>
            </View>
          </ScrollView>

          <View style={styles.actionsRow}>
            <TouchableOpacity
              onPress={closeWithAnim}
              style={[styles.btnGhost, { borderColor: C.border, backgroundColor:'#fff' }]}
              activeOpacity={0.85}
            >
              <Text style={{ fontWeight:'800', color: C.text }}>Batal</Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleSubmit}
              disabled={!canSubmit}
              style={[styles.btnPrimary, { backgroundColor: canSubmit ? A : C.border }]}
              activeOpacity={0.9}
            >
              <Ionicons name="checkmark-circle-outline" size={16} color="#fff" />
              <Text style={{ marginLeft:8, color:'#fff', fontWeight:'900' }}>Mulai</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </KeyboardAvoidingView>
    </View>
  );
}

export default memo(CreateSeasonModal);

const stylesVars = {
  colors: {
    bg: '#f7faf9', card: '#ffffff', border: '#e7eceb',
    text: '#0f172a', muted: '#667085',
    accent: '#1f8a4c', accentSoft: '#e6f4ee',
  }
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    elevation: 9999,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15,23,42,0.45)'
  },

  kav: {
    ...StyleSheet.absoluteFillObject,
  },
  kavWeb: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  kavMobile: {
    justifyContent: 'flex-end',
    padding: 12,
  },

  sheet: {
    borderWidth: 1,
    width: Platform.OS === 'web' ? '100%' : '100%',
    maxWidth: Platform.OS === 'web' ? 560 : undefined,
  },

  headRow: { flexDirection:'row', alignItems:'center', justifyContent:'space-between' },
  iconPill: { width:30, height:30, borderRadius: 15, borderWidth:1, alignItems:'center', justifyContent:'center' },
  closeBtn: { width:34, height:34, borderRadius: 17, borderWidth:1, alignItems:'center', justifyContent:'center' },
  hTitle: { fontSize: 16, fontWeight:'900' },
  hSub: { fontSize: 12, marginTop: 2 },

  chip: { borderWidth:1, borderRadius: 999, paddingVertical: 7, paddingHorizontal: 12, backgroundColor:'#fff', flexDirection:'row', alignItems:'center' },
  chipText: { color: stylesVars.colors.text, fontWeight:'800' },

  block: { marginTop: 10, borderWidth:1, borderRadius: 12, padding: 10 },
  blockHead: { flexDirection:'row', alignItems:'center', justifyContent:'space-between' },
  blockTitle: { marginLeft: 8, fontWeight:'900' },

  yearBtn: { width:32, height:32, borderRadius: 16, borderWidth:1, alignItems:'center', justifyContent:'center', backgroundColor:'#fff' },
  yearPill: { marginHorizontal: 8, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, borderWidth:1, backgroundColor:'#fff' },

  presetChip: { flexDirection:'row', alignItems:'center', paddingVertical: 7, paddingHorizontal: 12, borderRadius: 999, borderWidth:1, marginRight: 8, marginBottom: 8, backgroundColor:'#fff' },

  hintRow: { flexDirection:'row', alignItems:'flex-start', marginTop: 4 },
  hintText: { flex:1, marginLeft: 8, fontSize: 12, lineHeight: 16 },

  label: { fontWeight:'800', marginBottom: 6 },
  input: { borderWidth:1, borderRadius: 10, backgroundColor:'#fff', paddingHorizontal: 10, paddingVertical: 10 },
  helper: { marginTop: 6, fontSize: 12, lineHeight: 16 },

  actionsRow: { flexDirection:'row', columnGap:10, marginTop: 12 },
  btnGhost: { flex:1, height:44, borderRadius: 12, borderWidth:1, alignItems:'center', justifyContent:'center' },
  btnPrimary: { flex:1, height:44, borderRadius: 12, alignItems:'center', justifyContent:'center', flexDirection:'row' },
});
